import { SystemStyleObject } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'

export type { ColorScheme }
export type IncidentCardStatus = 'active' | 'in-progress' | 'resolved'
export type IncidentCardPriority = 'none' | 'low' | 'medium' | 'high' | 'urgent'

const tokens = {
  dark: {
    bg: 'bg.card-elevated',
    borderColor: 'border.subtle',
    titleColor: 'text.primary',
    timestampColor: 'text.secondary',
    descriptionTextColor: 'text.primary',
  },
  light: {
    bg: 'light.bg.card',
    borderColor: 'light.border.subtle',
    titleColor: 'light.text.primary',
    timestampColor: 'light.text.secondary',
    descriptionTextColor: 'light.text.primary',
  },
} as const

const chipTokens = {
  dark: { bg: 'bg.chip-in-field', color: 'text.chip-in-field' },
  light: { bg: 'light.bg.chip-in-field', color: 'light.text.chip-in-field' },
} as const

// ─── Card ─────────────────────────────────────────────────────────────────────

export const incidentCardStyles = (
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => ({
  bg: tokens[colorScheme].bg,
  border: '1px solid',
  borderColor: tokens[colorScheme].borderColor,
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  p: '16px',
  w: 'full',
})

export const incidentCardHeadingRowStyles: SystemStyleObject = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
  overflow: 'hidden',
  w: 'full',
  flexShrink: 0,
}

export const incidentCardHeadingSideStyles: SystemStyleObject = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  overflow: 'hidden',
  flexShrink: 0,
}

export const incidentCardDetectionIconStyles = (
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => ({
  color: colorScheme === 'dark' ? 'icon.secondary' : 'light.icon.secondary',
  flexShrink: 0,
  h: '20px',
  overflow: 'hidden',
  w: '20px',
  '& svg': { width: '20px', height: '20px' },
})

export const incidentCardTitleStyles = (
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => ({
  fontFamily: "'Soehne Kraftig', sans-serif",
  fontStyle: 'normal',
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '20px',
  color: tokens[colorScheme].titleColor,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  minWidth: 0,
  margin: 0,
})

export const incidentCardTimestampStyles = (
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => ({
  fontFamily: "'Soehne', sans-serif",
  fontStyle: 'normal',
  fontWeight: 400,
  fontSize: '12px',
  lineHeight: '16px',
  color: tokens[colorScheme].timestampColor,
  w: 'full',
  flexShrink: 0,
  margin: 0,
})

export const incidentCardDescriptionRowStyles: SystemStyleObject = {
  alignContent: 'center',
  alignItems: 'center',
  display: 'flex',
  flexWrap: 'wrap',
  gap: '4px',
  overflow: 'hidden',
  w: 'full',
  flexShrink: 0,
}

export const incidentCardDescriptionTextStyles = (
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => ({
  fontFamily: "'Soehne', sans-serif",
  fontStyle: 'normal',
  fontWeight: 400,
  fontSize: '12px',
  lineHeight: '16px',
  color: tokens[colorScheme].descriptionTextColor,
  whiteSpace: 'nowrap',
  margin: 0,
})

// ─── Description Chip ─────────────────────────────────────────────────────────

export const incidentCardDescriptionChipStyles = (
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => ({
  alignItems: 'center',
  bg: chipTokens[colorScheme].bg,
  borderRadius: '4px',
  display: 'inline-flex',
  flexShrink: 0,
  justifyContent: 'center',
  px: '8px',
  py: '2px',
})

export const incidentCardDescriptionChipTextStyles = (
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => ({
  color: chipTokens[colorScheme].color,
  fontFamily: "'Soehne Kraftig', sans-serif",
  fontStyle: 'normal',
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '20px',
  margin: 0,
  whiteSpace: 'nowrap',
})
